/**
 * SortableDetailCard Component
 * 
 * Displays a detail field card with drag-and-drop functionality.
 */

import React from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AccountGroupDetailField } from "../../../types";

export interface SortableDetailCardProps {
  detail: AccountGroupDetailField;
  index: number;
  isDetailInUse: (detailName: string) => boolean;
  onEditDetailField: (index: number) => void;
  onRemoveDetailField: (index: number) => void;
  editingDetailIndex: number | null;
}

/**
 * Sortable detail card component
 */
export default function SortableDetailCard({
  detail,
  index,
  isDetailInUse,
  onEditDetailField,
  onRemoveDetailField,
  editingDetailIndex,
}: SortableDetailCardProps): React.ReactElement {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: String(index),
    disabled: editingDetailIndex !== null,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isEditing = editingDetailIndex !== null;
  const isThisDetailEditing = editingDetailIndex === index;
  const isOtherDetailEditing = isEditing && !isThisDetailEditing;

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 4 : 0}
      sx={{
        p: 1,
        border: 2,
        borderColor: isThisDetailEditing ? "primary.main" : "divider",
        borderRadius: 1,
        backgroundColor: isThisDetailEditing 
          ? (theme) => theme.palette.mode === "dark" 
            ? "rgba(25, 118, 210, 0.2)"
            : "rgba(25, 118, 210, 0.08)"
          : "background.paper",
        "&:hover": { boxShadow: 2 },
        transition: "all 0.2s",
        cursor: isDragging ? "grabbing" : "default",
        opacity: isOtherDetailEditing ? 0.6 : 1,
        boxShadow: isThisDetailEditing ? 2 : 0,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <IconButton
          {...(isOtherDetailEditing ? {} : attributes)}
          {...(isOtherDetailEditing ? {} : listeners)}
          size="small"
          disabled={isOtherDetailEditing}
          sx={{
            cursor: isOtherDetailEditing ? "not-allowed" : "grab",
            "&:active": { cursor: isOtherDetailEditing ? "not-allowed" : "grabbing" },
            color: isOtherDetailEditing ? "action.disabled" : "text.secondary",
            p: 0.5,
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight="medium">
            {detail.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Type: {detail.type}
          </Typography>
          {detail.type === "list" && detail.listItems && detail.listItems.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              Items: {detail.listItems.length}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            onClick={() => onEditDetailField(index)}
            disabled={isOtherDetailEditing}
            sx={{ 
              color: isOtherDetailEditing ? "action.disabled" : "primary.main",
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <Tooltip
            title={
              isEditing
                ? "Finish or cancel editing to delete details"
                : isDetailInUse(detail.name) 
                  ? "This detail is used by accounts, so it can't be deleted" 
                  : ""
            }
            arrow
          >
            <span>
              <IconButton
                size="small"
                onClick={() => onRemoveDetailField(index)}
                disabled={isDetailInUse(detail.name) || isEditing}
                sx={{ color: "error.main" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}
